import { view, cssGenerator } from '@yola/ws-sdk';
import { ASPECT_RATIO_ATTRIBUTE } from 'src/js/modules/extensions/registry/ws-image/constants/media-container';
import toPositionPercents from 'src/js/modules/extensions/registry/ws-composite-group/helpers/to-position-percents.js';
import {
  DESKTOP_BREAKPOINT,
  TABLET_BREAKPOINT,
  MOBILE_BREAKPOINT,
  EDITOR_BREAKPOINTS,
} from '../../../common/constants/breakpoints';
import selectors from '../../../common/constants/selectors';
import getRatio from './get-ratio';

const { WIDTH: WIDTH_CLASS, CONTENT_POSITION: CONTENT_POSITION_CLASS } =
  cssGenerator.constants.classNames;

const { setResponsiveAttribute, setResponsiveClass, removeResponsiveClass } = view.operations;

const getElementAspectRatio = (element, aspectRatioIntents = new Map()) => {
  if (aspectRatioIntents.has(element)) {
    return aspectRatioIntents.get(element);
  }

  const { width, height } = element.getBoundingClientRect();

  return getRatio({ width, height });
};

const getResizeOperations = (
  elements,
  widthDragIntents = new Map(),
  aspectRatioIntents = new Map(),
  breakpoint = DESKTOP_BREAKPOINT,
  isBreakpointEditingEnabled = false
) =>
  elements
    .map((element) => {
      const elementId = view.accessors.getLiveElementId(element);
      const aspectRatio = String(getElementAspectRatio(element, aspectRatioIntents));
      const aspectRatioInstruction = [
        setResponsiveAttribute,
        [elementId, ASPECT_RATIO_ATTRIBUTE, aspectRatio, breakpoint],
      ];

      const operationList = [aspectRatioInstruction];

      if (element.matches(selectors.MEDIA_CONTAINER)) {
        const contentPosition = element.contentPosition || [50, 50];
        const contentPositionInstruction = [
          setResponsiveClass,
          [elementId, CONTENT_POSITION_CLASS, toPositionPercents(contentPosition), breakpoint],
        ];

        operationList.push(contentPositionInstruction);
      }

      if (widthDragIntents.has(element)) {
        const dragValue = widthDragIntents.get(element);
        if (dragValue) {
          operationList.push([setResponsiveClass, [elementId, WIDTH_CLASS, dragValue, breakpoint]]);

          if (!isBreakpointEditingEnabled) {
            // Template-authored breakpoint overrides would shadow the default value
            // on smaller screens, so they are cleared.
            operationList.push(
              [removeResponsiveClass, [elementId, WIDTH_CLASS, TABLET_BREAKPOINT]],
              [removeResponsiveClass, [elementId, WIDTH_CLASS, MOBILE_BREAKPOINT]]
            );
          }
        } else if (isBreakpointEditingEnabled) {
          operationList.push([removeResponsiveClass, [elementId, WIDTH_CLASS, breakpoint]]);
        } else {
          // TODO: Remove this branch after the breakpoint_editing feature flag is removed:
          operationList.push(
            ...EDITOR_BREAKPOINTS.map((editorBreakpoint) => [
              removeResponsiveClass,
              [elementId, WIDTH_CLASS, editorBreakpoint],
            ])
          );
        }
      }

      return operationList;
    })
    .flat();

export default getResizeOperations;
